import type { BrandSettings } from '@integration-layer/ampliance/schemas/brand-settings-schema.localized'
import brandSettings from '@integration-layer/data/armani/brands-settings/settings.json'

/**
 * Represents a collection of brand settings indexed by brand slug.
 * @typedef {Object.<string, BrandSettings>} BrandsSettings
 */
type BrandsSettings = { [brand: string]: BrandSettings }

/**
 * Adapts an array of brand settings into an object with brand slugs as keys.
 *
 * @param {BrandSettings[]} settings - Array of brand settings to be adapted.
 * @returns {BrandsSettings} An object with brand slugs as keys and their corresponding settings.
 * @private
 */
const adaptBrandsSettings = (settings: BrandSettings[]) => {
  return settings.reduce((acc: BrandsSettings, item: BrandSettings) => {
    acc[convertStringToSlug(item.name)] = item
    return acc
  }, {} as BrandsSettings)
}

/**
 * Composable for managing and retrieving brand settings.
 *
 * @returns {Object} An object containing brand settings and utility methods.
 * @returns {ComputedRef<BrandsSettings>} brandsSettings - Mapped brand settings object.
 * @returns {ComputedRef<BrandSettings[]>} brandSettingsSortedList - List of brand settings sorted by priority.
 * @returns {ComputedRef<BrandSettings[]>} internalBrandSortedList - List of internal brand settings.
 * @returns {ComputedRef<string[]>} brandsName - Array of brand names.
 * @returns {Function} getBrandSettings - Retrieves settings for a specific brand.
 * @returns {Function} getBrandLogo - Retrieves logo for a specific brand.
 * @returns {Function} getMonogramLogo - Retrieves monogram logo for a specific brand.
 * @returns {Function} getBrandTheme - Retrieves theme for a specific brand.
 * @returns {Function} shopEnabled - Checks if shop is enabled for a specific brand.
 * @returns {Function} getBrandName - Retrieves the name of a specific brand.
 * @returns {Function} getAlgoliaBrandName - Retrieves the Algolia name of a specific brand.
 * @returns {Function} getBrandAcronym - Retrieves the acronym of a specific brand.
 * @returns {Function} getShotRotation - Retrieves shot rotation settings for a specific brand.
 * @returns {Function} getNewsletterImage - Retrieves newsletter image for a specific brand.
 *
 * @example
 * const {
 *   brandsSettings,
 *   getBrandLogo,
 *   getBrandName
 * } = useBrandsSettings()
 *
 * const armaniLogo = getBrandLogo('armani')
 * const armaniName = getBrandName('armani')
 */
export const useBrandsSettings = () => {
  const brandSettingsResponse = ref(brandSettings.map(b => b.content))

  // Brands Settings array sorted by priority
  const brandSettingsSortedList = computed(
    () => brandSettingsResponse.value ?? []
  )

  /**
   * Computed list of internal brands (non-external brands).
   * @type {ComputedRef<BrandSettings[]>}
   */
  const internalBrandSortedList = computed(() =>
    brandSettingsSortedList.value.filter(brand => !brand.isExternal)
  )

  /**
   * Computed object of brand settings mapped by brand slug.
   * @type {ComputedRef<BrandsSettings>}
   */
  const brandsSettings = computed(
    // @ts-ignore
    () => adaptBrandsSettings(brandSettingsSortedList.value) ?? {}
  )

  /**
   * Computed array of brand names.
   * @type {ComputedRef<string[]>}
   */
  const brandsName = computed(() =>
    brandSettingsSortedList.value.map(brand => brand.name)
  )

  /**
   * Retrieves settings for a specific brand.
   *
   * @param {string} brand - The brand slug.
   * @returns {BrandSettings | undefined} Brand settings or undefined if not found.
   */
  const getBrandSettings = (brand: string) => {
    return brandsSettings.value?.[brand]
  }

  /**
   * Retrieves the name of a specific brand.
   *
   * @param {string} brand - The brand slug.
   * @returns {string | undefined} Brand name or undefined if not found.
   */
  const getBrandName = (brand: string) => {
    return getBrandSettings(brand)?.name
  }

  /**
   * Retrieves the Algolia name of a specific brand.
   *
   * @param {string} brand - The brand slug.
   * @returns {string | undefined} Algolia brand name or undefined if not found.
   */
  const getAlgoliaBrandName = (brand: string) => {
    return getBrandSettings(brand)?.nameAlgolia
  }

  /**
   * Retrieves the acronym of a specific brand.
   *
   * @param {string} brand - The brand slug.
   * @returns {string | undefined} Brand acronym or undefined if not found.
   */
  const getBrandAcronym = (brand: string) => {
    return getBrandSettings(brand)?.acronym
  }

  /**
   * Retrieves the logo of a specific brand.
   *
   * @param {string} brand - The brand slug.
   * @returns {string | undefined} Brand logo or undefined if not found.
   */
  const getBrandLogo = (brand: string) => {
    return getBrandSettings(brand)?.logo
  }

  /**
   * Retrieves the theme of a specific brand.
   *
   * @param {string} brand - The brand slug.
   * @returns {string | undefined} Brand theme or undefined if not found.
   */
  const getBrandTheme = (brand: string) => {
    return getBrandSettings(brand)?.theme
  }

  /**
   * Retrieves the monogram logo of a specific brand.
   *
   * @param {string} brand - The brand slug.
   * @returns {string | undefined} Brand monogram logo or undefined if not found.
   */
  const getMonogramLogo = (brand: string) => {
    return getBrandSettings(brand)?.monogram
  }

  /**
   * Checks if shop is enabled for a specific brand.
   *
   * @param {string} brand - The brand slug.
   * @returns {boolean | undefined} Whether shop is enabled or undefined if not found.
   */
  const shopEnabled = (brand: string) => {
    return getBrandSettings(brand)?.shop
  }

  /**
   * Retrieves the shot rotation settings for a specific brand.
   *
   * @param {string} brand - The brand slug.
   * @returns {string | undefined} Shot rotation settings or undefined if not found.
   */
  const getShotRotation = (brand: string) => {
    return getBrandSettings(brand)?.shotRotation
  }

  /**
   * Retrieves the newsletter image for a specific brand.
   *
   * @param {string} brand - The brand slug.
   * @returns {string | undefined} Newsletter image or undefined if not found.
   */
  const getNewsletterImage = (brand: string) => {
    return getBrandSettings(brand)?.newsletterImage
  }

  return {
    brandsSettings,
    brandSettingsSortedList,
    internalBrandSortedList,
    brandsName,
    getBrandSettings,
    getBrandLogo,
    getMonogramLogo,
    getBrandTheme,
    shopEnabled,
    getBrandName,
    getAlgoliaBrandName,
    getBrandAcronym,
    getShotRotation,
    getNewsletterImage,
  }
}
